import React from 'react';

const styles = {
  container: {
    backgroundColor: 'transparent', // Lets the App.js dark background shine through
    minHeight: '100vh',
    padding: '80px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: '"Inter", sans-serif',
    color: '#f8fafc', // Light text
  },
  header: {
    textAlign: 'center',
    marginBottom: '60px',
    maxWidth: '700px',
  },
  title: {
    fontSize: '3rem',
    fontWeight: '900',
    marginBottom: '10px',
    color: '#ffffff', // Pure white
    letterSpacing: '-1px',
  },
  subtitle: {
    fontSize: '1.2rem',
    color: '#94a3b8', // Slate grey
    lineHeight: '1.6',
  },
  // GRID LAYOUT
  grid: {
    width: '100%',
    maxWidth: '900px',
    display: 'grid',
    gap: '30px',
  },
  // JOB CARD
  card: {
    border: '1px solid #1c2b4d', // Subtle blue border
    borderRadius: '12px',
    padding: '30px',
    backgroundColor: '#111c3a', // Dark Navy
    transition: 'transform 0.2s, box-shadow 0.2s',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.3)', // Dark shadow for depth
  },
  roleTitle: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color: '#ffffff', // White
    marginBottom: '5px',
  },
  tagContainer: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  tag: {
    fontSize: '0.85rem',
    fontWeight: 'bold',
    padding: '5px 12px',
    borderRadius: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // Subtle translucent grey/white
    color: '#cbd5e1', // Slate text
    textTransform: 'uppercase',
  },
  activeTag: {
    backgroundColor: 'rgba(16, 185, 129, 0.1)', // Translucent glowing green
    color: '#10b981', // Neon/Mint green
    border: '1px solid #10b981',
  },
  description: {
    fontSize: '1rem',
    lineHeight: '1.6',
    color: '#cbd5e1', // Light slate text
  },
  requirementsTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    marginTop: '10px',
    color: '#f8fafc', // Off-white
  },
  list: {
    paddingLeft: '20px',
    margin: '0',
    color: '#cbd5e1', // Matches description
  },
  listItem: {
    marginBottom: '5px',
  },
  applyButton: {
    marginTop: '15px',
    alignSelf: 'flex-start',
    padding: '12px 30px',
    backgroundColor: '#b026ff', // Signature Foreplay Games Purple!
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 'bold',
    borderRadius: '6px',
    fontSize: '1rem',
    transition: 'background 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 15px rgba(176, 38, 255, 0.3)', // Purple glow
  },
  // NO OPENINGS MESSAGE
  emptyState: {
    textAlign: 'center',
    padding: '50px',
    backgroundColor: '#111c3a', // Dark Navy
    border: '1px solid #1c2b4d',
    borderRadius: '12px',
    color: '#64748b', // Darker slate
  },
  warningText: {
    color: '#ef4444', // A bright, readable red for dark mode
    fontWeight: 'bold',
  },
  filledCardModifier: {
    opacity: 0.6, // Dims the whole card
    borderColor: '#2a3b61', // Darker muted border
  },
  filledTag: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)', // Very dim grey
    color: '#64748b', // Slate grey
    border: '1px solid #334155',
  },
  statusMessageFilled: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#0f172a', // Very dark, recessed background
    borderRadius: '8px',
    color: '#94a3b8', // Slate grey
    fontSize: '0.95rem',
    fontStyle: 'italic',
    borderLeft: '4px solid #475569', // Muted slate accent line
  }
};

export default function JobsPage() {
  return (
    <div style={styles.container}>
      
      <div style={styles.header}>
        <h1 style={styles.title}>Work With Us</h1>
        <p style={styles.subtitle}>
          Foreplay Games is an independent studio dedicated to high-quality adult narratives. 
          We are currently looking for <b>Freelance</b> talent to help bring our characters to life.
        </p>
        <p style={styles.subtitle}>
          <span style={styles.warningText}>NOTE: YOU MUST BE 18 YEARS OF AGE OR OLDER TO WORK WITH US! NON-NEGOTIABLE!</span>
        </p>
      </div>

      <div style={styles.grid}>
        
        {/* --- JOB LISTING 3: MUSIC COMPOSER --- */}
        <div style={styles.card}>
          <div>
            <h2 style={styles.roleTitle}>Music Composer</h2>
            <div style={styles.tagContainer}>
              <span style={{...styles.tag, ...styles.activeTag}}>Open</span>
              <span style={styles.tag}>Freelance / Contract</span>
              <span style={styles.tag}>Remote</span>
            </div>
          </div>

          <p style={styles.description}>
            We are looking for a composer to create the original soundtrack for <b>3XRay</b>. 
            The game requires a mix of "Slice of Life" (University vibes), tense "Investigation" tracks, 
            and distinct character themes.
          </p>

          <div>
            <h3 style={styles.requirementsTitle}>Requirements:</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Strong portfolio demonstrating ability to match specific moods.</li>
              <li style={styles.listItem}>Experience creating seamless loops for game audio.</li>
              <li style={styles.listItem}>Ability to create distinct "Leitmotifs" for main characters.</li>
              <li style={styles.listItem}>Comfortable with 18+ context (some tracks may be for intimate scenes).</li>
            </ul>
          </div>

          <a 
            href="mailto:contact@foreplaygames.dev?subject=Composer Application - [Name]" 
            style={styles.applyButton}
          >
            Apply via Email
          </a>
        </div>

        {/* --- JOB LISTING 4: 2D ARTIST / UI DESIGNER --- */}
        <div style={styles.card}>
          <div>
            <h2 style={styles.roleTitle}>2D Artist / UI Designer</h2>
            <div style={styles.tagContainer}>
              <span style={{...styles.tag, ...styles.activeTag}}>Open</span>
              <span style={styles.tag}>Freelance / Contract</span>
              <span style={styles.tag}>Remote</span>
            </div>
          </div>

          <p style={styles.description}>
            We are looking for a versatile 2D Digital Artist & UI Designer to help define the visual identity of Foreplay Games. 
            We need a skilled human hand to design game logos, studio branding, and bespoke UI assets (buttons, menus, meters, and icons).
          </p>

          <div>
            <h3 style={styles.requirementsTitle}>Requirements:</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Strong portfolio showcasing UI/UX design specifically for video games.</li>
              <li style={styles.listItem}>Experience creating clean, readable graphic design, typography, and logos.</li>
              <li style={styles.listItem}>Ability to match distinct styles (e.g., sleek/modern for <em>3XRay</em>, retro/desktop for <em>LonelyHands</em>).</li>
              <li style={styles.listItem}>Bonus: Experience with pixel art or 2D skeletal animation (Spine/Live2D).</li>
              <li style={styles.listItem}>Comfortable working in an 18+ adult indie studio environment.</li>
            </ul>
          </div>

          <a 
            href="mailto:contact@foreplaygames.dev?subject=2D Artist / UI Designer Application - [Name]" 
            style={styles.applyButton}
          >
            Apply via Email
          </a>
        </div>

        {/* <div style={styles.card}> */}
        <div style={{...styles.card, ...styles.filledCardModifier}}> 
          <div>
            <h2 style={styles.roleTitle}>Female Vocalist & Topliner (Indie/Pop-Rock)</h2>
            <div style={styles.tagContainer}>
              <span style={{...styles.tag, ...styles.activeTag}}>Open</span>
              <span style={styles.tag}>Freelance / Contract</span>
              <span style={styles.tag}>Remote</span>
            </div>
          </div>

          <p style={styles.description}>
            We are seeking a talented vocalist and topliner (songwriter) to write and perform an original character theme song for our narrative management sim, <b>LonelyHands</b>. Our composer will provide the instrumental track, and we need you to write the lyrics and vocal melody, then provide the final vocal recordings. The track is an emotional, indie-rock/pop song driven by a punchy bassline, requiring a voice that can deliver both raw passion and melancholic vulnerability.
          </p>

          <div>
            <h3 style={styles.requirementsTitle}>Requirements:</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Experience writing original lyrics and vocal melodies (toplining) over existing instrumentals.</li>
              <li style={styles.listItem}>Professional home recording setup (XLR preferred, low noise floor/treated space).</li>
              <li style={styles.listItem}>Strong background in indie, alt-pop, or rock vocal styles.</li>
              <li style={styles.listItem}>
                Able to deliver a similar vocal tone and emotional vibe to our reference track: <a href="https://youtu.be/28MFoXq6zAU?si=rjHPM4ENpxZwpbF0" target="_blank" rel="noopener noreferrer" style={{color: '#0066cc'}}>Listen to "Wherever We Are Now" (Cassette Beasts OST)</a>
              </li>
              <li style={styles.listItem}>Ability to deliver clean, unmixed vocal stems (dry audio) for our composer.</li>
              <li style={styles.listItem}>Please include a portfolio showcasing both your singing and original songwriting capabilities.</li>
            </ul>
          </div>

          {/* <div style={{ marginBottom: '20px' }}> */}
          <div>
            <h3 style={styles.requirementsTitle}>Compensation:</h3>
            <p style={{...styles.description, marginTop: '5px'}}>
              <b>$300 - $500 USD Flat Rate</b> (Negotiable based on experience).<br/>
              <i>Note: This rate includes a full commercial buyout (Work for Hire), allowing us to use the final tracks in-game, in marketing, and on a potential OST.</i>
            </p>
          </div>
          <div style={styles.statusMessageFilled}>
            Status: <b>Position Currently Filled.</b> We are not accepting new applications at this time, 
            but may open additional slots as production ramps up.
          </div>
          {/* <a 
            href="mailto:contact@foreplaygames.dev?subject=Vocalist and Topliner Application - [Name]" 
            style={styles.applyButton}
          >
            Apply via Email
          </a> */}
        </div>
        
        {/* --- JOB LISTING 1: VOICE ACTRESS --- */}
        {/* <div style={styles.card}> */}
        <div style={{...styles.card, ...styles.filledCardModifier}}> 
          <div>
            <h2 style={styles.roleTitle}>Voice Actress (Female)</h2>
            <div style={styles.tagContainer}>
              <span style={{...styles.tag, ...styles.activeTag}}>Open</span>
              <span style={styles.tag}>Freelance / Contract</span>
              <span style={styles.tag}>Remote</span>
            </div>
          </div>

          <p style={styles.description}>
            We are casting for multiple roles in <b>3XRay</b>, as well as a few roles for <b>LonelyHands</b>. We need versatile actors capable of delivering emotional depth 
            as well as handling NSFW/suggestive scenes with confidence.
          </p>

          <div>
            <h3 style={styles.requirementsTitle}>Requirements:</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Professional home recording setup (XLR preferred, low noise floor).</li>
              <li style={styles.listItem}>Ability to perform "efforts" (breaths, sighs, reactions) naturally.</li>
              <li style={styles.listItem}>Comfortable with 18+ themes (Sexual content, romance).</li>
              <li style={styles.listItem}>Turnaround time of 1-2 weeks per batch.</li>
            </ul>
          </div>
          <div style={styles.statusMessageFilled}>
            Status: <b>Position Currently Filled.</b> We are not accepting new applications at this time, 
            but may open additional slots as production ramps up.
          </div>
          {/* <a 
            href="mailto:contact@foreplaygames.dev?subject=Voice Acting Application - [Name]" 
            style={styles.applyButton}
          >
            Apply via Email
          </a> */}
        </div>

        {/* --- JOB LISTING 2: VOICE ACTOR (MALE) --- */}
        {/* <div style={styles.card}> */}
        <div style={{...styles.card, ...styles.filledCardModifier}}> 
          <div>
            <h2 style={styles.roleTitle}>Voice Actor (Male)</h2>
            <div style={styles.tagContainer}>
              <span style={{...styles.tag, ...styles.activeTag}}>Open</span>
              <span style={styles.tag}>Freelance / Contract</span>
              <span style={styles.tag}>Remote</span>
            </div>
          </div>

          <p style={styles.description}>
            We are casting for male roles in our upcoming desktop-simulation RPG, <b>LonelyHands</b>. 
            We are looking for grounded, naturalistic performances to bring our stressed college students, 
            roommates, overbearing managers, etc.... to life! We need voices that feel real and conversational.
          </p>

          <div>
            <h3 style={styles.requirementsTitle}>Requirements:</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Professional home recording setup (XLR preferred, low noise floor).</li>
              <li style={styles.listItem}>Ability to deliver natural, conversational dialogue (more "indie drama," less "anime over-the-top").</li>
              <li style={styles.listItem}>Comfortable with 18+ themes, mature language, and suggestive contexts.</li>
              <li style={styles.listItem}>Turnaround time of 1-2 weeks per batch.</li>
            </ul>
          </div>
          <div style={styles.statusMessageFilled}>
            Status: <b>Position Currently Filled.</b> We are not accepting new applications at this time, 
            but may open additional slots as production ramps up.
          </div>
          {/* <a 
            href="mailto:contact@foreplaygames.dev?subject=Male Voice Acting Application - [Name]" 
            style={styles.applyButton}
          >
            Apply via Email
          </a> */}
        </div>
      </div>
    </div>
  );
}